import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { createManualDispatchRide } from '../services/firebase';
import { useLang } from '../lang/LangContext';

const PILER_PRESETS = [
  { name: 'APSRTC Bus Stand 🚌', address: 'APSRTC Bus Stand, Pileru', lat: 13.6547389, lng: 78.9445406, defaultFare: 40 },
  { name: 'Pileru Railway Station 🚆', address: 'Pileru Railway Station', lat: 13.6625162, lng: 78.9280908, defaultFare: 40 },
  { name: 'Govt. Hospital Pileru 🏥', address: 'Govt. Hospital Pileru', lat: 13.654472, lng: 78.9418376, defaultFare: 40 },
  { name: 'Main Market 🛍️', address: 'Clock Tower Junction, Main Bazaar, Piler', lat: 13.6549, lng: 78.9471, defaultFare: 30 },
];

export default function Dispatch({ drivers, rides, onNavigate, isSuperAdmin = false }) {
  const { t } = useLang();
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [pickupName, setPickupName] = useState('APSRTC Bus Stand 🚌');
  const [pickupAddress, setPickupAddress] = useState('APSRTC Bus Stand, Pileru');
  const [pickupLat, setPickupLat] = useState(13.6547389);
  const [pickupLng, setPickupLng] = useState(78.9445406);

  const [dropName, setDropName] = useState('Govt. Hospital Pileru 🏥');
  const [dropAddress, setDropAddress] = useState('Govt. Hospital Pileru');
  const [dropLat, setDropLat] = useState(13.654472);
  const [dropLng, setDropLng] = useState(78.9418376);

  const [fare, setFare] = useState(40);
  const [loading, setLoading] = useState(false);

  const onlineCaptains = drivers.filter((d) => d.isOnline && d.isApproved && !d.isBlocked);

  const handleApplyPickupPreset = (preset) => {
    setPickupName(preset.name);
    setPickupAddress(preset.address);
    setPickupLat(preset.lat);
    setPickupLng(preset.lng);
  };

  const handleApplyDropPreset = (preset) => {
    setDropName(preset.name);
    setDropAddress(preset.address);
    setDropLat(preset.lat);
    setDropLng(preset.lng);
    if (preset.defaultFare) setFare(preset.defaultFare);
  };

  const handleDispatch = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) {
      toast.error('Dispatching rides requires Super Admin privileges');
      return;
    }
    if (!customerPhone.trim() || customerPhone.replace(/\D/g, '').length < 10) {
      toast.error('Valid 10-digit customer phone number required.');
      return;
    }
    setLoading(true);
    try {
      const phoneDigits = customerPhone.replace(/\D/g, '').slice(-10);
      const res = await createManualDispatchRide({
        customerName: customerName.trim() || `Phone Passenger (+91 ${phoneDigits})`,
        customerPhone: `+91${phoneDigits}`,
        pickup: { address: pickupAddress, name: pickupName, lat: pickupLat, lng: pickupLng },
        destination: { address: dropAddress, name: dropName, lat: dropLat, lng: dropLng },
        fare: Number(fare) || 30,
        vehicleType: 'auto',
      });
      const rId = String(res?.rideId || res || '');
      const otpStr = res?.otp ? ` · OTP: ${res.otp}` : '';
      toast.success(`🛺 Ride dispatched successfully! ID: ${rId.slice(0, 6)}${otpStr}`, { duration: 6000 });
      setCustomerName('');
      setCustomerPhone('');
      onNavigate?.('rides');
    } catch (err) {
      console.error(err);
      toast.error(err?.message?.replace(/^Firebase:\s*/i, '') || 'Failed to create manual dispatch ride.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="stat-grid">
        <div className="stat-card green">
          <div className="stat-label">{t('AVAILABLE CAPTAINS')}</div>
          <div className="stat-value green">{onlineCaptains.length}</div>
          <div className="stat-sub">{t('Ready to receive phone dispatch in Piler')}</div>
        </div>
        <div className="stat-card blue">
          <div className="stat-label">{t('DISPATCH MODE')}</div>
          <div className="stat-value blue">{t('Manual Phone Call')}</div>
          <div className="stat-sub">{t('Local Piler telephone dispatch desk')}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">{t('🛺 Manual Telephone Booking & Dispatcher')}</div>
        <form onSubmit={handleDispatch}>
          <div className="form-grid-2">
            <div className="field-group">
              <label className="field-label">{t('👤 PASSENGER NAME (OPTIONAL)')}</label>
              <input
                type="text"
                placeholder="e.g. Ramesh Reddy"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </div>
            <div className="field-group">
              <label className="field-label">{t('📱 PASSENGER PHONE NUMBER *')}</label>
              <input
                type="tel"
                placeholder="e.g. 9876543210"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="field-group">
            <label className="field-label">{t('📍 PICKUP LOCATION (PILER PRESETS)')}</label>
            <div className="preset-pills-row">
              {PILER_PRESETS.map((p, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`preset-pill ${pickupName === p.name ? 'active' : ''}`}
                  onClick={() => handleApplyPickupPreset(p)}
                >
                  {t(p.name)}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Pickup address"
              value={pickupAddress}
              onChange={(e) => setPickupAddress(e.target.value)}
              required
            />
          </div>

          <div className="field-group">
            <label className="field-label">{t('🏁 DROP DESTINATION (PILER PRESETS)')}</label>
            <div className="preset-pills-row">
              {PILER_PRESETS.map((p, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={`preset-pill ${dropName === p.name ? 'active' : ''}`}
                  onClick={() => handleApplyDropPreset(p)}
                >
                  {t(p.name)}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Drop address"
              value={dropAddress}
              onChange={(e) => setDropAddress(e.target.value)}
              required
            />
          </div>

          <div className="field-group">
            <label className="field-label">{t('💵 FARE CALCULATION (₹)')}</label>
            <div className="fare-display-box">
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className={`preset-pill ${Number(fare) === 30 ? 'active' : ''}`} onClick={() => setFare(30)}>{t('₹30 Min')}</button>
                <button type="button" className={`preset-pill ${Number(fare) === 40 ? 'active' : ''}`} onClick={() => setFare(40)}>{t('₹40 Local')}</button>
                <button type="button" className={`preset-pill ${Number(fare) === 60 ? 'active' : ''}`} onClick={() => setFare(60)}>{t('₹60 Outstation')}</button>
              </div>
              <div className="fare-input-wrap">
                <span className="fare-currency">₹</span>
                <input
                  type="number"
                  min={30}
                  max={1000}
                  className="fare-number-input"
                  value={fare}
                  onChange={(e) => setFare(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary-large"
            disabled={!isSuperAdmin || loading || onlineCaptains.length === 0}
            style={!isSuperAdmin ? { background: '#64748b', cursor: 'not-allowed' } : undefined}
          >
            {!isSuperAdmin ? '🔒 Dispatch Restricted (Super Admin Only)' : loading ? t('Dispatching...') : onlineCaptains.length === 0 ? t('🚫 No Captains Online') : t('🛺 Dispatch Ride Now')}
          </button>
        </form>
      </div>
    </>
  );
}
